// TrueFalseQuestion.tsx — Digital Sanctuary Design System
// Binary True/False format with DS tokens, reanimated animations, and accessibility

import React, { useCallback } from 'react';
import { View, StyleSheet } from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';

import {
  useSpringPress,
  useFadeIn,
  useCorrectPulse,
  useWrongShake,
} from '../../../design-system/animations';
import { useAppTheme } from '../../../design-system/ThemeProvider';
import { spacing, radius, shadows, animation } from '../../../design-system/tokens';
import { useLanguageStore } from '../../../features/quiz-core/languageStore';
import { t } from '../../../i18n/translations';
import { IQESAnswerOption, IQESParchmentCard } from '../../../shared/ui/IQESComponents';
import { playIQESAudioCue, playIQESHaptic } from '../../../shared/ui/iqesSensoryFeedback';
import useReducedMotion from '../../../shared/ui/useReducedMotion';
import DevotionalExplanation from '../DevotionalExplanation';

import { TrueFalseQuestionProps, TrueFalseAnswer } from './types/QuestionFormatProps';

type ThemeColors = ReturnType<typeof useAppTheme>['colors'];

// Individual T/F button with DS animation hooks
const TFButton: React.FC<{
  value: boolean;
  label: string;
  isSelected: boolean;
  isCorrect: boolean;
  isWrong: boolean;
  showResult: boolean;
  disabled: boolean;
  onPress: (value: boolean) => void;
}> = ({ value, label, isSelected, isCorrect, isWrong, showResult, disabled, onPress }) => {
  // DS animation hooks
  const reduceMotion = useReducedMotion();
  const { animatedStyle: pressStyle, onPressIn, onPressOut } = useSpringPress(0.95);
  const { animatedStyle: correctStyle } = useCorrectPulse(isCorrect && showResult);
  const shakeStyle = useWrongShake(isWrong);

  const handlePressIn = useCallback(() => {
    if (!disabled && !showResult) {
      onPressIn();
    }
  }, [disabled, showResult, onPressIn]);

  const handlePressOut = useCallback(() => {
    onPressOut();
  }, [onPressOut]);

  const handlePress = useCallback(() => {
    if (disabled || showResult) return;
    void playIQESHaptic('select', { enabled: !reduceMotion });
    void playIQESAudioCue('select', { enabled: !reduceMotion });
    onPress(value);
  }, [disabled, reduceMotion, showResult, value, onPress]);

  // Pick the right animation style based on state
  const activeAnimStyle = isWrong ? shakeStyle : isCorrect && showResult ? correctStyle : pressStyle;
  const state = showResult
    ? isWrong ? 'incorrect' : isCorrect ? 'correct' : 'disabled'
    : 'default';

  return (
    <Animated.View style={[{ flex: 1 }, activeAnimStyle]}>
      <View style={[tfButtonStyles.choiceAura, value ? tfButtonStyles.trueAura : tfButtonStyles.falseAura]} pointerEvents="none" />
      <IQESAnswerOption
        label={label}
        state={state}
        trailingIcon={value ? 'checkmark' : 'close'}
        style={[
          tfButtonStyles.tfButton,
          !showResult && (value ? tfButtonStyles.trueChoiceButton : tfButtonStyles.falseChoiceButton),
          !showResult && isSelected && (value ? tfButtonStyles.trueSelectedChoiceButton : tfButtonStyles.falseSelectedChoiceButton),
        ]}
        hideBadge
        prominent
        showDefaultIcon
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        onPress={handlePress}
        disabled={disabled}
        accessibilityLabel={`${label}${isSelected ? ', selected' : ''}`}
      />
    </Animated.View>
  );
};

const TrueFalseQuestion: React.FC<TrueFalseQuestionProps & { explanation?: string }> = ({
  question,
  currentIndex: _currentIndex,
  totalQuestions: _totalQuestions,
  onAnswer,
  selectedAnswer,
  disabled = false,
  showCorrectAnswer = false,
  isAnswered = false,
  customTrueLabel,
  customFalseLabel,
  showExplanation = false,
  explanation,
}) => {
  const { colors } = useAppTheme();
  const styles = createStyles(colors);
  const { language } = useLanguageStore();
  // DS fade-in for question card
  const fadeInStyle = useFadeIn();

  const formatData = question.format_data;
  const trueLabel = customTrueLabel || t('quizFlow.trueAnswer', language);
  const falseLabel = customFalseLabel || t('quizFlow.falseAnswer', language);
  const referenceText = String(formatData.reference || formatData.verse_reference || '');

  const handleAnswer = useCallback((answer: boolean) => {
    if (disabled || isAnswered) return;
    const trueFalseAnswer: TrueFalseAnswer = { format: 'true_false', answer };
    onAnswer(trueFalseAnswer);
  }, [disabled, isAnswered, onAnswer]);

  const isCorrectTrue = showCorrectAnswer && formatData.correct_answer === true;
  const isCorrectFalse = showCorrectAnswer && formatData.correct_answer === false;
  const isWrongTrue = showCorrectAnswer && selectedAnswer === true && !isCorrectTrue;
  const isWrongFalse = showCorrectAnswer && selectedAnswer === false && !isCorrectFalse;

  return (
    <View testID="true-false-screen" style={styles.container}>
      {/* Question Card — DS fade-in hook */}
      <Animated.View style={fadeInStyle}>
        <IQESParchmentCard prompt={question.question} reference={referenceText} style={styles.questionCard} />
      </Animated.View>

      {/* True/False Buttons */}
      <View style={styles.buttonsRow}>
        <TFButton
          value={true}
          label={trueLabel}
          isSelected={selectedAnswer === true}
          isCorrect={isCorrectTrue}
          isWrong={isWrongTrue}
          showResult={showCorrectAnswer}
          disabled={disabled || isAnswered}
          onPress={handleAnswer}
        />
        <TFButton
          value={false}
          label={falseLabel}
          isSelected={selectedAnswer === false}
          isCorrect={isCorrectFalse}
          isWrong={isWrongFalse}
          showResult={showCorrectAnswer}
          disabled={disabled || isAnswered}
          onPress={handleAnswer}
        />
      </View>

      {/* Devotional Explanation */}
      {showExplanation && explanation && (
        <Animated.View entering={FadeIn.delay(300).duration(animation.normal)}>
          <DevotionalExplanation explanation={explanation} />
        </Animated.View>
      )}
    </View>
  );
};

const createStyles = (_colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.md,
  },
  questionCard: {
    width: '100%',
    alignSelf: 'center',
    borderRadius: radius.lg,
    marginBottom: spacing.lg,
    ...shadows.md,
  },
  questionText: {
    lineHeight: 28,
  },
  buttonsRow: {
    position: 'relative',
    flexDirection: 'row',
    gap: spacing.md,
    marginBottom: spacing.md,
  },
  tfButton: {
    borderRadius: radius.xl,
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.lg,
    justifyContent: 'center',
    minHeight: 92,
    borderWidth: 1.5,
    ...shadows.md,
  },
  buttonInner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.lg,
  },
  iconWrap: {
    width: 44,
    height: 44,
    borderRadius: radius.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tfIcon: {
    marginBottom: 0,
  },
  tfLabel: {
    fontWeight: '800',
    letterSpacing: 0.8,
    flex: 1,
  },
});

const tfButtonStyles = StyleSheet.create({
  choiceAura: {
    position: 'absolute',
    top: -6,
    bottom: -6,
    left: -6,
    right: -6,
    borderRadius: radius.xl + 6,
    opacity: 0.22,
  },
  trueAura: {
    backgroundColor: 'rgba(34, 197, 94, 0.26)',
  },
  falseAura: {
    backgroundColor: 'rgba(248, 113, 113, 0.18)',
  },
  tfButton: {
    minHeight: 122,
    paddingVertical: spacing.md,
    borderWidth: 1.5,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  trueChoiceButton: {
    backgroundColor: 'rgba(20, 101, 38, 0.84)',
    borderColor: 'rgba(34, 197, 94, 0.72)',
  },
  trueSelectedChoiceButton: {
    backgroundColor: 'rgba(20, 116, 42, 0.94)',
    borderColor: 'rgba(134, 239, 172, 0.92)',
    shadowColor: '#22C55E',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.22,
    shadowRadius: 18,
    elevation: 8,
  },
  falseChoiceButton: {
    backgroundColor: 'rgba(93, 23, 23, 0.82)',
    borderColor: 'rgba(248, 113, 113, 0.66)',
  },
  falseSelectedChoiceButton: {
    backgroundColor: 'rgba(116, 28, 28, 0.94)',
    borderColor: 'rgba(252, 165, 165, 0.9)',
    shadowColor: '#EF4444',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.2,
    shadowRadius: 18,
    elevation: 8,
  },
});

export default TrueFalseQuestion;
